const DEFAULT_THRESHOLD = 5

function getEventPoint (event) {
  const touch = event.touches ? event.touches[0] : event.changedTouches && event.changedTouches[0]
  return touch || event
}

function getViewportBounds (el, minTop) {
  const resolvedMinTop = typeof minTop === 'function' ? minTop() : minTop

  return {
    minTop: resolvedMinTop,
    maxTop: document.documentElement.clientHeight - el.offsetHeight,
    minRight: 0,
    maxRight: document.documentElement.clientWidth - el.offsetWidth
  }
}

function clamp (value, min, max) {
  return Math.max(min, Math.min(value, max))
}

function enableDragMode (el) {
  document.body.style.userSelect = 'none'
  document.body.style.pointerEvents = 'none'
  if (el) {
    el.style.pointerEvents = 'auto'
  }
}

function disableDragMode (el) {
  document.body.style.userSelect = ''
  document.body.style.pointerEvents = ''
  if (el) {
    el.style.pointerEvents = ''
  }
}

export function normalizeTopRightPosition (el, position, { minTop = 0 } = {}) {
  const bounds = getViewportBounds(el, minTop)

  return {
    top: clamp(position.top, bounds.minTop, bounds.maxTop),
    right: clamp(position.right, bounds.minRight, bounds.maxRight)
  }
}

export function makeDraggable (el, {
  boundsEl,
  getPosition,
  setPosition,
  shouldStart,
  onClick,
  onMoveStart,
  onMoveEnd,
  minTop = 0,
  threshold = DEFAULT_THRESHOLD
} = {}) {
  if (!el || typeof getPosition !== 'function' || typeof setPosition !== 'function') return null

  let removeActiveListeners = null

  const preventNextClick = () => {
    const preventClick = event => {
      event.preventDefault()
      event.stopPropagation()
      event.stopImmediatePropagation()
      document.removeEventListener('click', preventClick, { capture: true })
    }
    document.addEventListener('click', preventClick, { capture: true })
  }

  const startMoving = event => {
    if (typeof shouldStart === 'function' && !shouldStart(event)) return

    event.preventDefault()
    event.stopPropagation()

    const targetEl = boundsEl || el
    const startPoint = getEventPoint(event)
    const startX = startPoint.clientX
    const startY = startPoint.clientY
    const startPosition = getPosition()
    const isTouch = event.type.startsWith('touch')
    let hasMoved = false

    enableDragMode(targetEl)

    const handleMove = event => {
      const point = getEventPoint(event)
      const deltaX = point.clientX - startX
      const deltaY = point.clientY - startY

      if (Math.abs(deltaX) > threshold || Math.abs(deltaY) > threshold) {
        if (!hasMoved) {
          hasMoved = true
          if (typeof onMoveStart === 'function') onMoveStart()
        }
      }

      if (!hasMoved) return

      setPosition(normalizeTopRightPosition(targetEl, {
        top: startPosition.top + deltaY,
        right: startPosition.right - deltaX
      }, { minTop }))
    }

    const handleEnd = event => {
      disableDragMode(targetEl)

      if (hasMoved) {
        event.preventDefault()
        event.stopPropagation()
        preventNextClick()
      } else if (typeof onClick === 'function') {
        onClick(event)
      }

      if (typeof onMoveEnd === 'function') onMoveEnd(hasMoved)
      cleanup()
    }

    const cleanup = () => {
      document.removeEventListener(isTouch ? 'touchmove' : 'mousemove', handleMove, { capture: true })
      document.removeEventListener(isTouch ? 'touchend' : 'mouseup', handleEnd, { capture: true })
      if (removeActiveListeners === cleanup) {
        removeActiveListeners = null
      }
    }

    if (removeActiveListeners) removeActiveListeners()
    removeActiveListeners = cleanup

    document.addEventListener(isTouch ? 'touchmove' : 'mousemove', handleMove, { capture: true, passive: false })
    document.addEventListener(isTouch ? 'touchend' : 'mouseup', handleEnd, { capture: true, passive: false })
  }

  el.addEventListener('mousedown', startMoving)
  el.addEventListener('touchstart', startMoving, { passive: false })

  return () => {
    if (removeActiveListeners) removeActiveListeners()
    el.removeEventListener('mousedown', startMoving)
    el.removeEventListener('touchstart', startMoving, { passive: false })
    disableDragMode(boundsEl || el)
  }
}
